import * as React              from 'react';
import { useTranslation }      from 'react-i18next';
import { LocalStorageService } from 'ts-storage-service';
import DV                      from 'ts-data-validator';

import useRequest      from '../../common/services/request';
import { useUser }     from '../../providers/user';
import { useWhereBuy } from '../../providers/whereBuy';

import RenderButton from '../buttons/base/RenderButton';
import LocationIcon from '../icons/LocationIcon';
import CloseIcon    from '../icons/CloseIcon';
import Popover      from '../Popover';

import { UserLocationModal } from './components';

const UserLocation = () => {
  const { t } = useTranslation();

  const request  = useRequest();
  const user     = useUser();
  const whereBuy = useWhereBuy();

  const ls = new LocalStorageService('user');

  const modalRef   = React.useRef(null);
  const popoverRef = React.useRef(null);

  const probablyCity = React.useMemo(() => {
    if (user.location && user.location.cityId && user.location.label) {
      return user.location;
    }

    if (!user.probablyCity || !user.probablyCity.cityId || !user.probablyCity.label) {
      const capital = user.cities.find((city) => {
        return city.isCapital;
      });

      if (capital) {
        return {
          cityId: Number(capital.cityId),
          label:  capital.label,
        };
      }
    }

    return user.probablyCity ?? {};
  }, [user.location, user.probablyCity]);

  const update               = React.useCallback((key, data) => {
    const currentDate  = new Date();
    const oneYearLater = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));

    ls.setData(key, data, oneYearLater);
  }, []);
  const changeCity           = React.useCallback((cityId) => {
    const city  = user.cities.find((item) => {
      return item.cityId === cityId;
    });
    const place = whereBuy.cities?.find((item) => {
      return item.cityId === cityId;
    });

    if (city && place) {
      if (user.location?.cityId !== cityId) {
        user.changeLocation({
          cityId:   city.cityId,
          label:    city.label,
          regionId: city.regionId,
        }, place.urlKey);
      }

      update('visited', true);
    }

    popoverRef.current.handleEnd();
  }, [update, user.cities, user.location?.cityId, whereBuy.cities]);
  const handleChangeLocation = React.useCallback(() => {
    if (user.userInitialStateLoaded) {
      popoverRef.current?.handleEnd();
      modalRef.current?.onShowModal();
    }
  }, [user.userInitialStateLoaded]);

  React.useEffect(() => {
    const location = ls.getData('location');

    if (location) {
      user.changeLocation(location);
    } else if (window.location.protocol.startsWith('https')) {
      window.navigator.geolocation?.getCurrentPosition(async(position) => {
        const response = await request.post('/set-user-browser-location', {
          latitude:  position.coords.latitude,
          longitude: position.coords.longitude,
        });

        const city = response.cities.find((city) => city.cityId === response.location.cityId);

        if (city) {
          const location = {
            cityId:   city.cityId,
            label:    city.label,
            regionId: city.regionId,
          };

          update('location', location);

          user.changeLocation(location);
        }
      }, () => {
      });
    }
  }, []);
  React.useEffect(() => {
    if (
      !user.locationLoading &&
      !user.location &&
      (DV.isNull(user.probablyCity) || !DV.isNumber(user.probablyCity.cityId) || !DV.isString(user.probablyCity.label))
    ) {
      user.getGeolocation();
    }
  }, [user.locationLoading, user.location, user.probablyCity]);
  React.useEffect(() => {
    if (user.location && user.cities.length > 0) {
      const city = user.cities.find((city) => {
        return city.cityId === user.location.cityId;
      });

      update('location', {
        cityId:   city.cityId,
        label:    city.label,
        regionId: city.regionId,
      });
    }
  }, [user.location]);
  React.useEffect(() => {
    if (popoverRef.current) {
      if (
        (
          !user.locationLoading &&
          (!user.location || !DV.isNumber(user.location.cityId) || !DV.isString(user.location.label))
        ) ||
        !ls.getData('visited')
      ) {
        popoverRef.current.handleStart();
      }
    }
  }, [user.location, user.locationLoading, popoverRef.current]);

  if (
    !user.locationLoading &&
    (!user.location || !DV.isNumber(user.location.cityId) || !DV.isString(user.location.label)) &&
    (!probablyCity || !DV.isNumber(probablyCity.cityId) || !DV.isString(probablyCity.label))
  ) {
    return null;
  }

  return (
    <div className={'location'}>
      <Popover
        className={'large'}
        closeTrigger={false}
        content={(
          <>
            <span
              className={'close'}
              onClick={() => {
                popoverRef.current.handleEnd();

                update('visited', true);
              }}
            ><CloseIcon /></span>
            <div className={'location'}>
              {t('client_your_city')}{' '}
              <span>{user.location.label || probablyCity.label}</span>
            </div>
            <div className={'btns'}>
              <RenderButton
                className={'fill'}
                label={t('client_all_right')}
                action={() => {
                  changeCity(probablyCity.cityId);
                }}
              />
              <RenderButton
                action={handleChangeLocation}
                label={t('client_choose_another')}
              />
            </div>
          </>
        )}
        overlay={true}
        placement={'bottom'}
        ref={popoverRef}
        trigger={'click'}
      >
        <span className={'location_wrapper'}>
          <LocationIcon />
          <span>{user.location.label || probablyCity.label}</span>
        </span>
      </Popover>
      <UserLocationModal
        changeLocation={changeCity}
        ref={modalRef}
        sub_title={'client_change_location_subTitle'}
        title={'client_change_location'}
        user={user}
      />
    </div>
  );
};

export default UserLocation;
